import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  Badge,
  Business,
  Close,
  PersonAdd,
  Search,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import type { GalUser } from '../../services/graphService';
import { fetchUserPhoto } from '../../services/graphService';
import { ROLE_OPTIONS, ELEVATED_ROLE_VALUES } from '../../constants/roles';

interface GalSearchInlineProps {
  query: string;
  results: GalUser[];
  loading: boolean;
  role: string;
  existingEmails: Set<string>;
  addingEmail: string | null;
  /** When false, the Lab Admin / Super Admin options are disabled (only a Super Admin may grant them). */
  canAssignElevatedRoles: boolean;
  onSearch: (q: string) => void;
  onRoleChange: (role: string) => void;
  onAdd: (user: GalUser) => void;
}

export const GalSearchInline = ({
  query, results, loading, role, existingEmails, addingEmail, canAssignElevatedRoles,
  onSearch, onRoleChange, onAdd,
}: GalSearchInlineProps) => {
  const theme = useTheme();
  const { instance } = useMsal();
  const [photos, setPhotos] = useState<Record<string, string | null>>({});

  // Fetch photos when results change
  useEffect(() => {
    if (results.length === 0) return;
    let cancelled = false;
    const load = async () => {
      for (const user of results) {
        if (photos[user.id]) continue;
        const url = await fetchUserPhoto(instance as any, user.id);
        if (cancelled) return;
        setPhotos((prev) => ({ ...prev, [user.id]: url }));
      }
    };
    load();
    return () => { cancelled = true; };
  }, [results, instance]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Paper elevation={0} sx={{
      mb: 3,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      borderRadius: '12px',
      overflow: 'hidden',
    }}>
      {/* Search header */}
      <Box sx={{
        px: 2.5, py: 1.5,
        display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: (query.length >= 2) ? `1px solid ${alpha(theme.border.color, 0.3)}` : 'none',
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
      }}>
        <PersonAdd sx={{ fontSize: 18, color: theme.palette.primary.main }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8125rem', minWidth: 'fit-content' }}>
          Add from Directory
        </Typography>

        <TextField
          size="small"
          placeholder="Search by name or email..."
          value={query}
          onChange={(e) => onSearch(e.target.value)}
          name="gal-search-field"
          autoComplete="off"
          inputProps={{
            autoComplete: 'new-password',
            'data-lpignore': 'true',
            'data-form-type': 'other',
            'aria-autocomplete': 'none',
          }}
          InputProps={{
            startAdornment: <Search sx={{ fontSize: 16, color: alpha(theme.palette.text.primary, 0.35), mr: 0.5 }} />,
            endAdornment: loading ? <CircularProgress size={16} /> : query ? (
              <IconButton size="small" onClick={() => onSearch('')} sx={{ p: 0.25 }}>
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            ) : null,
          }}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              fontSize: '0.8125rem', height: 34, borderRadius: '8px',
              backgroundColor: '#fff',
            },
          }}
        />

        <FormControl size="small" sx={{ minWidth: 110 }}>
          <Select
            value={role}
            onChange={(e) => onRoleChange(e.target.value)}
            sx={{ fontSize: '0.8125rem', height: 34, borderRadius: '8px', backgroundColor: '#fff' }}
          >
            {ROLE_OPTIONS.map((r) => (
              <MenuItem
                key={r.value}
                value={r.value}
                disabled={!canAssignElevatedRoles && ELEVATED_ROLE_VALUES.includes(r.value)}
                sx={{ fontSize: '0.8125rem' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: r.color }} />
                  {r.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Results */}
      {query.length >= 2 && (
        <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
          {loading && results.length === 0 && (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!loading && results.length === 0 && (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No users found for "{query}"</Typography>
            </Box>
          )}
          {results.map((galUser) => {
            const email = galUser.mail || galUser.userPrincipalName;
            const exists = existingEmails.has(email.toLowerCase());
            const isAdding = addingEmail === email;
            const photoUrl = photos[galUser.id];
            return (
              <ListItem
                key={galUser.id}
                sx={{
                  px: 2.5, py: 1,
                  borderBottom: `1px solid ${alpha(theme.border.color, 0.15)}`,
                  '&:last-child': { borderBottom: 'none' },
                  opacity: exists ? 0.5 : 1,
                  transition: 'background-color 0.15s',
                  '&:hover': { backgroundColor: exists ? 'transparent' : alpha(theme.palette.primary.main, 0.04) },
                }}
                secondaryAction={
                  exists ? (
                    <Chip label="Already added" size="small" sx={{ height: 22, fontSize: '0.7rem' }} />
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={isAdding ? <CircularProgress size={14} /> : <PersonAdd sx={{ fontSize: 14 }} />}
                      disabled={isAdding}
                      onClick={() => onAdd(galUser)}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: '6px' }}
                    >
                      Add
                    </Button>
                  )
                }
              >
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Avatar
                    src={photoUrl ?? undefined}
                    sx={{
                      width: 34, height: 34, fontSize: '0.8rem', fontWeight: 600,
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.main,
                    }}
                  >
                    {galUser.displayName.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                      {galUser.displayName}
                    </Typography>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'flex', gap: 1.5, fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>
                      <span>{email}</span>
                      {galUser.jobTitle && (
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Badge sx={{ fontSize: 11 }} /> {galUser.jobTitle}
                        </Box>
                      )}
                      {galUser.department && (
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Business sx={{ fontSize: 11 }} /> {galUser.department}
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};
